import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { BRAND } from "@/config";
import { supabase } from "@/lib/supabase";
import { getProfile } from "@/lib/auth";
import { hasSignedCurrentWaiver } from "@/lib/family";

/**
 * Signed-in area: requires a confirmed account and exposes the profile to child routes.
 * Parents must sign the current registration agreement (and give a phone) before anything else.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    if (!user) throw redirect({ to: "/login" });
    if (!user.email_confirmed_at) throw redirect({ to: "/verify-email", search: { email: user.email } });
    const profile = await getProfile(user.id);
    if (!profile) throw redirect({ to: "/login" });
    if (profile.role !== "admin" && location.pathname !== "/waiver" && !(await hasSignedCurrentWaiver(user.id))) {
      throw redirect({ to: "/waiver" });
    }
    return { profile };
  },
  component: AppLayout,
});

function AppLayout() {
  const { profile } = Route.useRouteContext();
  const navigate = useNavigate();

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/login", replace: true });
  }

  return (
    <div className="min-h-svh bg-a1-black font-sans text-white">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3">
          <Link to={profile.role === "admin" ? "/admin" : "/home"} className="flex items-center gap-3">
            <img src="/a1-mark.webp" alt={BRAND.name} className="h-8 w-auto" />
            <span className="hidden font-display text-lg font-extrabold uppercase italic sm:inline">{BRAND.appName}</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden text-white/60 sm:inline">{profile.first_name ?? profile.email}</span>
            <button type="button" onClick={signOut} className="rounded-md border border-white/20 px-3 py-1.5 hover:border-white/40">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-8">
        <Outlet />
      </main>
    </div>
  );
}
